<template>
  <div class="max-w-lg mx-auto box-container text-14 px-4 py-6 mb-4">
    <div class="relative flex items-center md:hover:underline">
      <div class="flex-1">
        <p class="text-gray-500 text-12 uppercase">{{ $t('transfer_comparison.pay_by') }}</p>
        <p class="font-semibold">{{ payins }}</p>
      </div>
      <div class="flex-1 text-right font-semibold" :class="{ invisible: expanded }">
        <p class="text-18">{{ amount }}</p>
        <p
          v-if="service.promotion && amount !== standardAmount"
          class="text-12 text-blue-900 line-through"
        >
          {{ standardAmount }}
        </p>
        <p
          v-if="service.quote.fee.externalPayin || service.quote.fee.externalPayout"
          class="text-12 text-amber-800"
        >
          {{ $t('label.estimated') }}
        </p>
      </div>
      <svg role="presentation" focusable="false" tabindex="-1" :class="['icon icon-xs m-2 ml-4', { 'rotate-180': expanded }]"
         
        alt="back"
        svg-inline
       xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 32 19"><title>back</title>
  <path fill="currentColor" fill-rule="evenodd"
    d="M.8 1.18a2.26 2.26 0 0 1 3.2 0L16 13.2 28.03 1.18a2.26 2.26 0 0 1 3.2 3.2L17.6 17.97c-.88.9-2.31.9-3.2 0L.8 4.39a2.26 2.26 0 0 1 0-3.2Z"
    clip-rule="evenodd" />
</svg>
      <button class="absolute w-full inset-0" :aria-label="$t('details')" @click="toggleExpanded" />
    </div>
    <button
      v-if="!hasCommonPromo && service.promotion"
      class="flex w-full text-left bg-blue-50 text-blue-900 text-14 rounded-4 py-1.5 px-2 mt-2 md:hover:underline"
      @click="$emit('open-promo', service.promotion)"
    >
      <svg role="presentation" focusable="false" tabindex="-1" class="icon icon-sm mr-2"   alt="promotion" svg-inline  xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 32 32"><title>promotion</title>
<path fill="currentColor" fill-rule="evenodd" clip-rule="evenodd" d="m2.18 7.6 1.95 9.83 18.75 10.83 7.54-13.05L11.66 4.38 2.18 7.61Zm10.84 6.3a3.3 3.3 0 1 0-5.7-3.3 3.3 3.3 0 0 0 5.7 3.3Z"/>
<path fill="#fff" d="M19.58 19.41a1.82 1.82 0 0 1 1.83-.91 1.79 1.79 0 0 1 1.26.72c.15.2.25.41.3.65.07.23.1.46.06.7a1.79 1.79 0 0 1-.72 1.26 1.85 1.85 0 0 1-2.05.13 1.9 1.9 0 0 1-.56-.5 2.1 2.1 0 0 1-.31-.63 2.1 2.1 0 0 1-.06-.71c.04-.25.12-.48.25-.7Zm2.39 1.38a.87.87 0 0 0 .1-.68.87.87 0 0 0-.43-.54.87.87 0 0 0-.69-.1.87.87 0 0 0-.54.42.87.87 0 0 0-.1.69c.07.24.2.42.43.54.22.13.45.16.68.1.24-.06.42-.2.55-.43Zm-4.73-6.88a1.82 1.82 0 0 1 1.83-.92 1.79 1.79 0 0 1 1.26.73c.15.2.25.4.3.64.08.23.1.47.06.7a1.79 1.79 0 0 1-.72 1.26 1.85 1.85 0 0 1-2.05.13 1.9 1.9 0 0 1-.56-.49 2.1 2.1 0 0 1-.31-.64 2.1 2.1 0 0 1-.06-.71c.04-.24.12-.48.25-.7Zm2.39 1.38a.87.87 0 0 0 .1-.69.87.87 0 0 0-.43-.54.87.87 0 0 0-.69-.1.87.87 0 0 0-.54.43.87.87 0 0 0-.1.68c.07.24.2.42.43.55.22.12.45.15.68.1.24-.07.42-.21.55-.43Zm3.76-.21.52.74-7.27 4.28-.53-.75 7.28-4.27Z"/>
</svg>
      <p class="self-end">
        <span
          v-html="$t(`promotions:${service.promotion.headline}`, promoVariables)"
          v-if="promoVariables"
        />
        <span class="underline">{{ $t('details') }}</span>
      </p>
    </button>
    <ServiceDetails
      v-if="expanded"
      class="mt-3"
      :service="service"
      :provider="provider"
      :mid-market-rate="midMarketRate"
    />
  </div>
</template>

<script lang="ts" setup>
import { panelOpen } from '../analytics/actions'
import { getPromotionVariables } from '@core/components/provider-promotion/helpers/utils'
import type { Corridor } from '@core/types/imt'
import type { Service, Provider } from '../types'
import ServiceDetails from '../service-details.vue'
import { Comparison } from '../analytics/objects/Comparison'
import { ComparisonSection } from '../analytics/objects/ComparisonSection'
import { ComparisonResult } from '../analytics/objects/ComparisonResult'

const props = defineProps<{
  provider: Provider
  service: Service
  midMarketRate: number
  hasCommonPromo?: boolean
  isExpanded?: boolean
}>()

defineEmits(['open-promo'])

const { $lang, $track, $t } = useNuxtApp()

const corridor = inject('corridor') as Corridor

const getResultAnalyticsObj = inject('getResultAnalyticsObj') as (
  slug: string,
  location?: string,
) => ComparisonResult | undefined
const sectionAnalyticsObj = inject<Ref<ComparisonSection>>('sectionAnalyticsObj')
const comparisonAnalyticsObj = inject<Ref<Comparison>>('comparisonAnalyticsObj')

const expanded = ref(props.isExpanded)

async function toggleExpanded() {
  expanded.value = !expanded.value
  if (expanded.value) {
    await panelOpen(
      $track,
      'Cost Breakdown',
      comparisonAnalyticsObj?.value,
      sectionAnalyticsObj?.value,
      getResultAnalyticsObj(props.provider.slug),
    )
  }
}

const amount = computed(() => {
  return formatMoney(props.service.amount, corridor.currencyTo.iso, $lang)
})

const standardAmount = computed(() => {
  return formatMoney(props.service.quote.receivedAmount, corridor.currencyTo.iso, $lang)
})

const payins = computed(() => {
  return props.service.payins.map((p) => $t(`payin.${p}`)).join(', ')
})

const promoVariables = computed(() => {
  return getPromotionVariables(props.service.promotion, props.provider.displayName)
})
</script>
